import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useInvoiceStatementPreview } from '@/api/invoices';

/**
 * Preview-Mail dialog — renders the statement HTML the invoice email will send
 * (`GET /invoices/:id/preview.statement.html`) inside a sandboxed iframe. The
 * query is lazy (fires only while the dialog is open). Read-only.
 */
export function InvoicePreviewMailDialog({
  id,
  open,
  onOpenChange,
}: {
  id: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const preview = useInvoiceStatementPreview(id, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* @ui Dialog caps at lg (720px); the statement is a wide print-style doc,
          so widen the content inline to fit it without horizontal clipping. */}
      <DialogContent size="lg" style={{ maxWidth: 'min(1100px, 95vw)', width: '95vw' }}>
        <DialogHeader>
          <DialogTitle>Email preview</DialogTitle>
          <DialogDescription>The statement this invoice will send to the customer.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {preview.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--vn-space-8)' }}>
              <Spinner />
            </div>
          ) : preview.isError ? (
            <Alert tone="danger" title="Failed to load preview" />
          ) : (
            <iframe
              title="Invoice statement preview"
              srcDoc={preview.data ?? ''}
              sandbox=""
              style={{
                width: '100%',
                height: '75vh',
                border: '1px solid var(--vn-border-subtle)',
                borderRadius: 'var(--vn-radius-md)',
                background: 'var(--vn-bg-canvas)',
              }}
            />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
